import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ResolvedCreateAction } from "../lib/create-actions";

interface CreateEntityButtonProps {
  actions: ResolvedCreateAction[];
  onSelect: (action: ResolvedCreateAction) => void | Promise<void>;
  label?: string;
  className?: string;
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "default" | "sm" | "lg" | "icon" | "icon-sm";
  align?: "start" | "center" | "end";
  alwaysMenu?: boolean;
  iconOnly?: boolean;
  triggerIcon?: React.ElementType;
  triggerAriaLabel?: string;
}

export function CreateEntityButton({
  actions,
  onSelect,
  label = "New",
  className,
  variant = "outline",
  size = "default",
  align = "start",
  alwaysMenu = false,
  iconOnly = false,
  triggerIcon: TriggerIcon = Plus,
  triggerAriaLabel,
}: CreateEntityButtonProps) {
  const isDisabled = actions.length === 0;
  const isMenu = alwaysMenu || actions.length !== 1;

  if (!isMenu) {
    return (
      <Button
        variant={variant}
        size={size}
        className={className}
        aria-label={triggerAriaLabel ?? label}
        onClick={(event) => {
          event.stopPropagation();
          void onSelect(actions[0]!);
        }}
        disabled={isDisabled}
      >
        <TriggerIcon className="size-4" />
        {!iconOnly ? label : null}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant={variant}
            size={size}
            className={className}
            aria-label={triggerAriaLabel ?? label}
            disabled={isDisabled}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <TriggerIcon className="size-4" />
            {!iconOnly ? label : null}
          </Button>
        }
      />
      <DropdownMenuContent align={align} className="min-w-48">
        {actions.map((action) => (
          <DropdownMenuItem
            key={`${action.sourceNodeKey}:${action.action.presetKey}`}
            onClick={(event) => {
              event.stopPropagation();
              void onSelect(action);
            }}
          >
            {action.action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
